import React, { useState } from "react"
import { CollapsableFilter } from "./collapsable_filter"
import { FilterSearchInput } from "./filter_search_input"
import { FilterDropdown, FilterDropdownOption } from "./filter_dropdown"
import { IncludeExcludeTag } from "./include_exclude_tag"
import { useDisplayOrder, toggleIncludeExclude, removeFromBoth } from "./filter-utils"

interface IncludeExcludeFilterProps {
  label: string
  icon: string
  placeholder: string
  tooltip?: string
  includedValues: string[]
  excludedValues: string[]
  allowedValues: Array<{ id: string; name: string }>
  onChangeIncluded: (values: string[]) => void
  onChangeExcluded: (values: string[]) => void
}

export function IncludeExcludeFilter({
  label,
  icon,
  placeholder,
  tooltip,
  includedValues = [],
  excludedValues = [],
  allowedValues,
  onChangeIncluded,
  onChangeExcluded,
}: IncludeExcludeFilterProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const [showDropdown, setShowDropdown] = useState(false)

  const allSelectedSet = new Set([...includedValues, ...excludedValues])
  const hasValues = allSelectedSet.size > 0

  // Use shared hook for stable display order
  const allSelectedValues = useDisplayOrder(includedValues, excludedValues)

  const filteredOptions = allowedValues.filter(
    (option) => !allSelectedSet.has(String(option.id)) && option.name.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const handleSelectValue = (valueId: string) => {
    onChangeIncluded([...includedValues, String(valueId)])
    setSearchTerm("")
    setShowDropdown(false)
  }

  const handleRemoveValue = (valueId: string) => {
    removeFromBoth(valueId, includedValues, excludedValues, onChangeIncluded, onChangeExcluded)
  }

  const handleToggleIncludeExclude = (valueId: string) => {
    toggleIncludeExclude(valueId, includedValues, excludedValues, onChangeIncluded, onChangeExcluded)
  }

  const handleClear = () => {
    onChangeIncluded([])
    onChangeExcluded([])
    setSearchTerm("")
  }

  const getValueLabel = (valueId: string) => {
    return allowedValues.find((v) => String(v.id) === String(valueId))?.name || valueId
  }

  return (
    <CollapsableFilter
      label={label}
      icon={icon}
      tooltip={tooltip}
      count={allSelectedSet.size}
      hasValues={hasValues}
      onClear={handleClear}
    >
      <FilterSearchInput
        id={`filter-${label}`}
        label={label}
        placeholder={placeholder}
        value={searchTerm}
        onChange={(value) => {
          setSearchTerm(value)
          setShowDropdown(true)
        }}
        onFocus={() => setShowDropdown(true)}
        onBlur={() => setShowDropdown(false)}
      />

      <FilterDropdown show={showDropdown && filteredOptions.length > 0}>
        {filteredOptions.slice(0, 10).map((option) => (
          <FilterDropdownOption key={option.id} value={option.id} onSelect={handleSelectValue}>
            {option.name}
          </FilterDropdownOption>
        ))}
      </FilterDropdown>

      {hasValues && (
        <div className="mt-2">
          {allSelectedValues.map((valueId) => (
            <IncludeExcludeTag
              key={valueId}
              label={getValueLabel(valueId)}
              isIncluded={includedValues.includes(valueId)}
              onToggle={() => handleToggleIncludeExclude(valueId)}
              onRemove={() => handleRemoveValue(valueId)}
            />
          ))}
        </div>
      )}
    </CollapsableFilter>
  )
}
